import React, { useState } from 'react';
import { FaCheck, FaTimes, FaExclamationTriangle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const TableCheck = ({ validationSummary, onCorrection }) => {
    const [expandedChecks, setExpandedChecks] = useState({});
    const [editingValue, setEditingValue] = useState(null);

    const toggleCheck = (docType, checkType) => {
        setExpandedChecks(prev => ({
            ...prev,
            [`${docType}-${checkType}`]: !prev[`${docType}-${checkType}`]
        }));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
                return <FaCheck className="text-green-600" />;
            case 'corrected':
                return <FaCheck className="text-blue-600" />;
            case 'error':
                return <FaTimes className="text-rose-600" />;
            case 'partial':
                return <FaExclamationTriangle className="text-amber-600" />;
            default:
                return null;
        }
    };

    const documentTypes = {
        dni: 'DNI',
        dni_2: 'DNI 2',
        notary: 'Notaría',
        notary_2: 'Notaría 2',
        registry: 'Registro',
        registry_2: 'Registro 2',
        agency: 'Gestoría',
        agency_2: 'Gestoría 2',
        appraisal: 'Tasación',
        appraisal_2: 'Tasación 2',
        mortgage_deed: 'Escritura Hipotecaria',
    };

    const validationTypes = {
        amount: 'Cantidad',
        nombre: 'Nombre',
        protocolo: 'Protocolo',
        dni: 'DNI',
        dni_valido: 'DNI Válido',
        total: 'Total',
        check_amount: 'Cantidad',
        check_nombre: 'Nombre',
        check_protocolo: 'Protocolo',
        check_dni: 'DNI',
        check_total: 'Total',
        fecha_firma: 'Fecha de Firma',
        importe_prestamo: 'Importe Préstamo',
        banco: 'Entidad Bancaria'
    };

    const formatCheckType = (type) => {
        return validationTypes[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    };

    const formatValue = (value) => {
        if (value === "No disponible") return "No encontrado";
        if (typeof value === 'number') {
            return new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: 'EUR'
            }).format(value);
        }
        return value;
    };

    const handleCorrection = (docType, checkType, newValue) => {
        if (onCorrection) {
            const updatedDocValidation = {
                ...validationSummary[docType],
                checks: {
                    ...validationSummary[docType].checks,
                    [checkType]: {
                        ...validationSummary[docType].checks[checkType],
                        actual: newValue,
                        status: 'corrected'
                    }
                }
            };

            // Verificamos si todos los checks están correctos o corregidos
            const allChecksSuccessful = Object.values(updatedDocValidation.checks).every(
                check => check.status === 'success' || check.status === 'corrected'
            );

            // Actualizamos el estado del documento
            updatedDocValidation.status = allChecksSuccessful ? 'success' : 'error';

            onCorrection({
                docType,
                checkType,
                value: newValue,
                originalValue: validationSummary[docType].checks[checkType].actual,
                updatedDocValidation
            });
        }
        setEditingValue(null);
    };

    const renderValueInfo = (checkData, docType, checkType) => {
        const isExpanded = expandedChecks[`${docType}-${checkType}`];
        const shouldShowByDefault = checkData.status === 'error' || checkData.status === 'unavailable';

        if (!shouldShowByDefault && !isExpanded) return null;

        return (
            <AnimatePresence>
                <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="mt-3"
                >
                    <div className={`
                        rounded-lg p-4 border
                        ${checkData.status === 'error' 
                            ? 'border-rose-100 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-900/10' 
                            : checkData.status === 'unavailable' 
                            ? 'border-amber-100 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10' 
                            : 'border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50'
                        }
                    `}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                            <div>
                                <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Esperado
                                </span>
                                <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {formatValue(checkData.expected)}
                                </div>
                            </div>
                            <div>
                                <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                    Encontrado
                                </span>
                                <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {editingValue?.docType === docType && editingValue?.checkType === checkType ? (
                                        <input
                                            type="text"
                                            className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            defaultValue={checkData.actual === "No disponible" ? "" : checkData.actual}
                                            onBlur={(e) => handleCorrection(docType, checkType, e.target.value)}
                                            autoFocus
                                        />
                                    ) : (
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                                            <span className="break-all">
                                                {checkData.actual === "No disponible" || !checkData.actual ? "No encontrado" : formatValue(checkData.actual)}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {(checkData.status === 'error' || checkData.status === 'unavailable') && (
                                                    <button
                                                        onClick={() => handleCorrection(docType, checkType, checkData.expected)}
                                                        className="text-xs text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                                                    >
                                                        Usar esperado
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setEditingValue({ docType, checkType })}
                                                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    {checkData.status === 'error' ? 'Corregir' : 'Editar'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        );
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="overflow-x-auto w-full"
        >
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                    <tr>
                        <th className="px-2 sm:px-6 py-4 text-left text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider w-full sm:w-1/4">
                            Documento
                        </th>
                        <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider w-2/3">
                            Validaciones
                        </th>
                        <th className="px-2 sm:px-4 py-4 text-left text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider w-12">
                            Estado
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {Object.entries(validationSummary).map(([docType, data], index) => (
                        <motion.tr 
                            key={docType}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ 
                                duration: 0.3,
                                delay: index * 0.1,
                                ease: "easeOut"
                            }}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 flex flex-col sm:table-row"
                        >
                            <td className="px-2 sm:px-6 py-4 sm:py-6">
                                <span className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    {documentTypes[docType]}
                                </span>
                            </td>
                            <td className="px-2 sm:px-6 py-4 sm:py-6">
                                <div className="space-y-4 sm:space-y-6">
                                    {Object.entries(data.checks).map(([checkType, checkData]) => (
                                        <motion.div 
                                            key={checkType}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-2">
                                                <span className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-200">
                                                    {formatCheckType(checkType)}
                                                </span>
                                                <div className="flex items-center space-x-2">
                                                    <motion.span 
                                                        whileHover={{ scale: 1.05 }}
                                                        className={`text-xs font-medium flex items-center ${
                                                            checkData.status === 'success' ? 'text-emerald-600 dark:text-emerald-400' :
                                                            checkData.status === 'error' ? 'text-rose-600 dark:text-rose-400' :
                                                            checkData.status === 'corrected' ? 'text-blue-600 dark:text-blue-400' :
                                                            'text-amber-600 dark:text-amber-400'
                                                        }`}
                                                    >
                                                        {checkData.status === 'success' ? (
                                                            <>
                                                                <FaCheck className="mr-1" /> Correcto
                                                            </>
                                                        ) : checkData.status === 'corrected' ? (
                                                            <>
                                                                <FaCheck className="mr-1" /> Corregido
                                                            </>
                                                        ) : checkData.status === 'error' ? (
                                                            <>
                                                                <FaTimes className="mr-1" /> Error
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaExclamationTriangle className="mr-1" /> Pendiente
                                                            </>
                                                        )}
                                                    </motion.span>
                                                    {checkData.status === 'success' || checkData.status === 'corrected' ? (
                                                        <motion.button
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => toggleCheck(docType, checkType)}
                                                            className="ml-2 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 transition-colors"
                                                        >
                                                            {expandedChecks[`${docType}-${checkType}`] ?
                                                                <FaChevronUp size={12} /> :
                                                                <FaChevronDown size={12} />
                                                            }
                                                        </motion.button>
                                                    ) : null}
                                                </div>
                                            </div>
                                            {renderValueInfo(checkData, docType, checkType)}
                                        </motion.div>
                                    ))}
                                </div>
                            </td>
                            <td className="px-2 sm:px-4 py-4 sm:py-6 w-12">
                                <motion.div 
                                    className="flex items-center justify-center"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ 
                                        type: "spring",
                                        stiffness: 260,
                                        damping: 20,
                                        delay: index * 0.1 + 0.2
                                    }}
                                >
                                    {getStatusIcon(data.status)}
                                </motion.div>
                            </td>
                        </motion.tr>
                    ))}
                </tbody>
            </table>
        </motion.div>
    );
};

export default TableCheck;
